import { io, type Socket } from 'socket.io-client';
import { notifyAuthFailed } from '../api/client';
import { config } from '../config';
import { auth } from '../firebase';

/**
 * The CRM realtime feed (docs/api.md → Realtime). One socket per signed-in
 * tenant, shared by every subscriber; disconnects when the last subscriber
 * unsubscribes.
 *
 * Contract notes that matter here:
 * - namespace is `/rt`; on UAT the `/crm` prefix goes in `path`, NOT the url.
 * - `{token, tenant}` travel in the handshake `auth`, not headers.
 * - The server force-disconnects expired tokens after pushing `auth.expired` —
 *   unhandled, the client goes silently deaf after ~1h.
 * - A socket reporting `connected` is not proof the feed is live: see the wake
 *   guards below.
 * - There is no per-conversation room: every tenant event arrives; subscribers
 *   filter client-side.
 */

export type CommsRealtimeEvent =
  | { name: 'message.created' | 'message.updated'; payload: unknown }
  | {
      name:
        | 'conversation.created'
        | 'conversation.updated'
        | 'conversation.status_changed'
        | 'assignee.changed';
      payload: unknown;
    }
  | { name: 'contact.created' | 'contact.updated'; payload: unknown }
  /** A booking dispatch made out of a conversation — the thread's strip. */
  | { name: 'booking.created'; payload: unknown }
  | { name: 'call.ringing' | 'call.answered' | 'call.ended'; payload: unknown }
  /** User-room event: only the recipient's own sockets receive it. */
  | { name: 'notification.created'; payload: unknown }
  /**
   * This agent's grants changed. Carries no state — the only correct response is
   * to re-read GET /meta, because what changed may be what you are allowed to
   * know.
   */
  | { name: 'access.changed'; payload: unknown }
  /** Synthetic: fired after a reconnect so stores can delta-sync. */
  | { name: 'reconnected'; payload: { offlineSeconds: number } };

type Handler = (event: CommsRealtimeEvent) => void;

const REALTIME_EVENTS = [
  'message.created',
  'message.updated',
  'conversation.created',
  'conversation.updated',
  'conversation.status_changed',
  'assignee.changed',
  'contact.created',
  'contact.updated',
  'booking.created',
  'call.ringing',
  'call.answered',
  'call.ended',
  'notification.created',
  'access.changed',
] as const;

/** How the handshake gets a token — Firebase locally, the bridge when embedded. */
export type SocketTokenGetter = (forceFresh: boolean) => Promise<string>;

const firebaseTokenGetter: SocketTokenGetter = async (forceFresh) => {
  const user = auth.currentUser;
  if (!user) return '';
  return user.getIdToken(forceFresh).catch(() => '');
};

let socket: Socket | null = null;
let socketTenant: string | null = null;
const handlers = new Set<Handler>();
let disconnectedAt: number | null = null;
/** When the feed stopped being trustworthy — tab backgrounded, or network lost. */
let asleepSince: number | null = null;

function emitAll(event: CommsRealtimeEvent): void {
  for (const handler of handlers) handler(event);
}

/** Floored at 1: a sub-second gap must still ask for a window, not `since=0`. */
function offlineSecondsSince(start: number): number {
  return Math.max(1, Math.ceil((Date.now() - start) / 1000));
}

function createSocket(tenant: string, getToken: SocketTokenGetter): Socket {
  /**
   * A forced refresh has already been spent on this socket's handshake. The
   * next 1034 is then a token minted seconds ago being refused, which retrying
   * cannot fix — see notifyAuthFailed. Cleared on a connection that works.
   */
  let refreshSpent = false;
  const base = new URL(config.crmBase);
  const path = `${base.pathname.replace(/\/+$/, '')}/socket.io`;

  const created = io(`${base.origin}/rt`, {
    path,
    transports: ['websocket'],
    // Called on every (re)connect attempt, so reconnects pick up fresh tokens
    // without us managing refresh timers.
    auth: (cb) => {
      getToken(false).then(
        (token) => cb({ token, tenant }),
        () => cb({ token: '', tenant }),
      );
    },
  });

  for (const name of REALTIME_EVENTS) {
    created.on(name, (payload: unknown) => emitAll({ name, payload }));
  }

  created.on('auth.expired', () => {
    // Force a fresh token into the next handshake, then reconnect.
    created.disconnect();
    getToken(true)
      .catch(() => '')
      .finally(() => created.connect());
  });

  created.on('connect_error', (err: Error & { data?: { code?: number } }) => {
    if (err.data?.code === 1034) {
      if (refreshSpent) {
        notifyAuthFailed();
        return;
      }
      refreshSpent = true;
      getToken(true).catch(() => {});
    }
    // 1036 means retrying can't help; socket.io stops by itself only on
    // explicit disconnect, but a forbidden handshake repeating is harmless
    // (and resolves if permissions change).
  });

  created.on('disconnect', () => {
    disconnectedAt = Date.now();
  });

  created.on('connect', () => {
    refreshSpent = false;
    if (disconnectedAt !== null) {
      const offlineSeconds = offlineSecondsSince(disconnectedAt);
      disconnectedAt = null;
      emitAll({ name: 'reconnected', payload: { offlineSeconds } });
      // `asleepSince` deliberately survives this: the tab may freeze again
      // between here and the agent looking at it, and a duplicate delta-sync
      // costs one idempotent refetch where a missed one costs a stale inbox.
    }
  });

  return created;
}

/**
 * A backgrounded tab gets frozen: its timers stop, so socket.io's own heartbeat
 * never fires and the socket keeps reporting `connected` across a link the OS
 * tore down while we slept. Nothing re-fires `connect`, so nothing delta-syncs
 * and the inbox quietly rots until the agent hits refresh. Waking — the tab
 * returning, or the network coming back under a tab that never left — is the
 * only moment we get to notice.
 *
 * Under this threshold a wake-up is an alt-tab rather than an outage: the link
 * almost certainly survived, and resyncing anyway would put a delta query from
 * every store on the wire each time an agent glances at another window.
 */
const RESYNC_AFTER_ASLEEP_MS = 10_000;

function markAsleep(): void {
  // Earliest signal wins — hidden-then-offline is one outage, and it started at
  // the first of the two.
  asleepSince ??= Date.now();
}

function markAwake(): void {
  const since = asleepSince;
  asleepSince = null;
  if (since === null) return;

  if (socket?.disconnected) {
    // Its reconnect backoff froze along with the tab; connect() thaws it now
    // rather than whenever the timer catches up. The reconnect emits
    // `reconnected` itself once `connect` lands, so this path must not — one
    // outage, one delta-sync.
    socket.connect();
    return;
  }
  if (Date.now() - since < RESYNC_AFTER_ASLEEP_MS) return;
  emitAll({ name: 'reconnected', payload: { offlineSeconds: offlineSecondsSince(since) } });
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') markAsleep();
  else markAwake();
}

/**
 * Bound to the socket's lifetime, not the module's. Named rather than inline so
 * the refs stay stable: addEventListener de-dupes an identical pair, so a
 * subscribe/unsubscribe cycle cannot stack a second set of these.
 */
function attachWakeGuards(): void {
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('offline', markAsleep);
  window.addEventListener('online', markAwake);
}

function detachWakeGuards(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('offline', markAsleep);
  window.removeEventListener('online', markAwake);
}

/** The one place the socket dies, so the wake guards can't outlive it. */
function teardownSocket(): void {
  detachWakeGuards();
  asleepSince = null;
  socket?.disconnect();
  socket = null;
  socketTenant = null;
}

/** Subscribe to the tenant's realtime feed. Returns the unsubscribe. */
export function subscribeRealtime(
  tenant: string,
  handler: Handler,
  getToken: SocketTokenGetter = firebaseTokenGetter,
): () => void {
  if (socket && socketTenant !== tenant) {
    // Tenant switched (sign-out/sign-in) — the old socket is useless.
    teardownSocket();
  }
  if (!socket) {
    socketTenant = tenant;
    disconnectedAt = null;
    socket = createSocket(tenant, getToken);
    attachWakeGuards();
  }
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0 && socket) teardownSocket();
  };
}
